<template>
    <div
        ref="wrapper"
        class="cz2__ai-modal__selfie"
    >
        <transition name="cz2__fade" mode="out-in">
            <selfie-camera-access
                v-if="isAccessState && aiSelfieActiveState === aiSelfieStates.default"
                :access-state="cameraAccessState"
            />

            <selfie-get-ready v-else-if="!isAccessState && aiSelfieActiveState === aiSelfieStates.default" />

            <selfie-say-cheese v-else-if="aiSelfieActiveState === aiSelfieStates.sayCheese" />

            <base-loading-state
                v-if="isLoading || isBuilding"
                :heading="loadingHeading"
                :copy="loadingCopy"
            />

            <base-error-state
                v-else-if="aiSelfieActiveState === aiSelfieStates.error"
            />
        </transition>
    </div>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz2__ai-modal {
            .cz2__ai-modal__selfie {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                text-align: center;
                background-color: #fff;
                padding: 48px 24px 24px 24px;

                .cz2__modal-popup__header {
                    margin-bottom: 0;
                }

                .cz2__btn {
                    height: 40px;
                    width: 230px;
                    font-size: 16px;

                    &:hover {
                        font-size: 18px;
                    }
                }

                .cz2__btn,
                .cz2__btn-content-wrapper {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                @include respond-above(lg) {
                    padding: 24px;
                }
            }
        }
    }
</style>

<script>
    import { animate } from 'motion';

    import SelfieCameraAccess from './SelfieCameraAccess.vue';
    import SelfieGetReady from './SelfieGetReady.vue';
    import SelfieSayCheese from './SelfieSayCheese.vue';
    import BaseLoadingState from './BaseLoadingState.vue';
    import BaseErrorState from './BaseErrorState.vue';

    export default {
        components: {
            SelfieCameraAccess,
            SelfieGetReady,
            SelfieSayCheese,
            BaseLoadingState,
            BaseErrorState,
        },

        data() {
            return {
                needCameraAccess: true,
                cameraAccessState: 'loading',
            };
        },

        computed: {
            isAccessState() {
                return this.cameraAccessState !== 'granted';
            },

            aiSelfieActiveState() {
                return this.$store.state.aiSelfieActiveState;
            },

            aiSelfieStates() {
                return this.$store.state.aiSelfieStates;
            },

            isLoading() {
                return this.aiSelfieActiveState === this.aiSelfieStates.loading;
            },

            isBuilding() {
                return this.aiSelfieActiveState === this.aiSelfieStates.building;
            },

            loadingHeading() {
                return this.isBuilding ? this.$t('AI_BUILDING_POP_HEADING') : this.$t('AI_LOADING_POP_HEADING');
            },

            loadingCopy() {
                return this.isBuilding ? this.$t('AI_BUILDING_POP_COPY') : this.$t('AI_LOADING_POP_COPY');
            },

            // Redefine what mobile means to accommodate special tablet styling
            isAiMobile() {
                return window.innerWidth < 640;
            },
        },

        mounted() {
            this.checkCameraPermission().then((accessState) => {
                this.cameraAccessState = accessState;
            });

            if (this.isAiMobile) {
                this.animateModal();
            }
        },

        methods: {
            async checkCameraPermission() {
                try {
                    // Trigger permissions prompt
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });

                    // Stop video tracks to prevent the camera from being actively used
                    stream.getVideoTracks().forEach((track) => {
                        track.stop();
                    });

                    return 'granted';
                } catch (error) {
                    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                        console.log('Camera permission denied.');
                    } else {
                        console.error('Error accessing camera: ', error);
                    }

                    return 'denied';
                }
            },

            animateModal() {
                animate(this.$refs.wrapper, {
                    transform: [
                        'translateY(100%)',
                        'translateY(0)',
                    ],
                }, {
                    duration: 0.35,
                    easing: 'ease-out',
                });
            },
        },
    };
</script>
